/**
 * Harmony slot selection — the one implementation every surface uses.
 *
 * ## Why this exists
 *
 * The web app and the Discord bot used to answer "which dyes make a triadic
 * harmony with this one?" through completely different code, and they
 * disagreed for **89–100 % of base dyes on every harmony type** (measured
 * 2026-09-03 over all 125 dyes):
 *
 * - the page walked `HARMONY_OFFSETS`, built each target by carrying the base
 *   dye's SATURATION AND VALUE onto the rotated hue, and ranked candidates by
 *   the user's configured ΔE method;
 * - the bot called a named `DyeService.find*Dyes()` per type, which rotates hue
 *   without preserving S/V — and `findComplementaryPair` did not rotate at all,
 *   it took an RGB `invert()`.
 *
 * For a desaturated base the difference is not subtle: `/harmony analogous` on
 * Snow White returned Neon Green and Kobold Brown, where the page shows Pure
 * White and Pearl White.
 *
 * This module is the page's algorithm, lifted verbatim so the page's output is
 * unchanged and every other surface now matches it. `HARMONY_OFFSETS` supplies
 * the geometry, so a harmony type is a row in a table rather than a method —
 * which is why `compound` and `shades` work here without any new code.
 *
 * @module services/dye/HarmonySelector
 */
import { HARMONY_OFFSETS } from '../../constants/index.js';
import { ColorService } from '../ColorService.js';
import { DEFAULT_COLOR_WHEEL, getColorWheel } from './wheels/ColorWheel.js';
/**
 * The single definition of "how far is this dye from the slot's target".
 *
 * BUG-064: everything that scores a harmony row goes through here, so a panel
 * can never mix ΔE units with degrees of hue — which is exactly what it used to
 * do when a filtered-out companion's replacement was scored differently from
 * its neighbours.
 */
function devianceFor(dye, targetHue, targetHex, config) {
    if (config.usePerceptualMatching) {
        return ColorService.getDistanceForMethod(targetHex, dye.hex, config.matchingMethod);
    }
    const dyeHue = ColorService.hexToHsv(dye.hex).h;
    const hueDiff = Math.abs(dyeHue - targetHue);
    return Math.min(hueDiff, 360 - hueDiff);
}
/** Every candidate scored against one slot's ideal, nearest first. */
function rankCandidates(candidates, targetHue, targetHex, config) {
    const scored = [];
    for (const dye of candidates) {
        // Facewear carries generic names ("Red", "Blue") and is not a dye since
        // schema v2; it must never surface as a harmony match.
        if (dye.category === 'Facewear')
            continue;
        scored.push({ dye, deviance: devianceFor(dye, targetHue, targetHex, config) });
    }
    scored.sort((a, b) => a.deviance - b.deviance);
    return scored;
}
/**
 * Whether `harmonyType` is one this module can build.
 *
 * Every key of `HARMONY_OFFSETS` works, which is the point: a type is a row in
 * that table, not a bespoke method, so the ten the page offers are the ten
 * every surface can offer.
 */
export function isKnownHarmonyType(harmonyType) {
    return Object.hasOwn(HARMONY_OFFSETS, harmonyType);
}
/**
 * Choose a dye for every slot of a harmony.
 *
 * Callers filter `candidates` themselves — this function does not know about
 * anyone's dye-filter shape, and pre-filtering is what makes "the nearest
 * ALLOWED dye to the ideal" the answer, rather than "the nearest allowed dye to
 * a dye that was thrown away".
 *
 * @param baseHex     the colour the harmony is built around; supplies H, S and V
 * @param harmonyType a key of `HARMONY_OFFSETS`
 * @param candidates  the pool to choose from, already filtered
 * @param config      ranking and de-duplication behaviour
 * @param options     dyes to exclude, and any slots the caller has pinned
 * @returns one slot per offset; `dye` is `null` for a slot with no candidate
 */
export function generateHarmonySlots(baseHex, harmonyType, candidates, config, options) {
    // Own-property check, not a truthiness one: `HARMONY_OFFSETS['toString']`
    // returns Object.prototype.toString, which is TRUTHY, so a `!offsets` guard
    // sails past it and the `.forEach` below throws. The web app reads this type
    // straight out of a share URL (`?harmony=`), so the key really can be
    // arbitrary.
    if (!isKnownHarmonyType(harmonyType))
        return [];
    const offsets = HARMONY_OFFSETS[harmonyType];
    const wheel = getColorWheel(config.wheel ?? DEFAULT_COLOR_WHEEL);
    // A wheel that keeps the base's OKLab L and C instead of its HSV S/V has no
    // meaningful hue-distance ranking: scoring by degrees against `targetHue`
    // throws away the lightness the wheel was chosen for, and returns a partner
    // at some other lightness entirely. Built once, used for every scoring call
    // below (pins included) so a slot can never be scored in different units
    // from its neighbours — the BUG-064 rule.
    const effective = wheel.carriesBaseHsv
        ? config
        : { ...config, usePerceptualMatching: true };
    const baseWheelHue = wheel.hueOf(baseHex);
    const companionCount = config.companionCount ?? 0;
    const preventDuplicates = config.preventDuplicates ?? false;
    // Two different questions, and they were one set until 2026-09-03.
    //
    // `excluded` is "must never be chosen" — normally the base dye itself, which
    // is never a member of its own harmony. A custom colour has no dye to
    // exclude, which is why this is a list rather than a required base dye:
    // `/harmony color:#FF0000` has a hex and nothing else.
    //
    // `used` is "already on screen", and only matters when the caller asked for
    // no repeats.
    //
    // Seeding `used` with the exclusions conflated them, and `used` is read only
    // on the `preventDuplicates` branch — so with duplicates allowed the
    // exclusions did nothing at all, against this function's own documented
    // contract. bot-logic defaults `preventDuplicates` to false, so `/harmony
    // monochromatic` (a single `[0]` offset, whose ideal IS the base colour)
    // answered the base dye as its own harmony at deltaE 0, and `/harmony
    // analogous` on a near-grey answered the base twice.
    const excluded = new Set(options?.excludeItemIDs ?? []);
    const used = new Set();
    // BUG-137 (2026-10-04): a pin is on screen from the start, so it is "used"
    // before ANY slot chooses. It used to enter `used` only when the loop reached
    // its own slot, so an earlier slot could still pick it and the palette showed
    // one dye twice. The web app reaches that by keeping a hand-swap across a
    // strict-matching, ΔE-method or filter change that moves slot 0's natural
    // pick onto the swapped-in dye. Walk the real slot indices rather than the
    // map's keys, so a stale pin for a slot this harmony lacks reserves nothing.
    //
    // The same walk settles two pins naming ONE dye: under de-duplication only
    // the first by slot index (not Map insertion order) is honoured. A later one
    // is dropped here and its slot is chosen as if it were never pinned —
    // otherwise both "won their slot outright" and the dye showed twice.
    const pins = new Map();
    if (options?.pinned) {
        for (let index = 0; index < offsets.length; index++) {
            const pin = options.pinned.get(index);
            if (!pin)
                continue;
            if (preventDuplicates) {
                if (used.has(pin.itemID))
                    continue;
                used.add(pin.itemID);
            }
            pins.set(index, pin);
        }
    }
    const slots = [];
    offsets.forEach((offset, index) => {
        const normalisedOffset = ((offset % 360) + 360) % 360;
        const wheelHue = (baseWheelHue + normalisedOffset) % 360;
        // The wheel builds the ideal. Warp wheels carry the BASE's saturation and
        // value onto the mapped hue — the whole reason a desaturated base finds
        // desaturated dyes; the constant-lightness wheel carries L and C instead.
        const { targetHex, targetHue } = wheel.target(baseHex, wheelHue);
        const ranked = rankCandidates(candidates, targetHue, targetHex, effective);
        const pin = pins.get(index);
        // Every branch below assigns, so no initialiser: an unread `= null` here
        // would be one more place for a future edit to leave a slot silently empty.
        let chosen;
        if (pin) {
            // An explicit hand-swap wins its slot outright, exclusions included: the
            // user naming a dye outranks our guess about which dyes are eligible.
            chosen = { dye: pin, deviance: devianceFor(pin, targetHue, targetHex, effective) };
        }
        else if (preventDuplicates) {
            // First eligible-and-unused, else the nearest eligible even if already
            // shown: a slot with a dye repeated beats a slot left blank. An excluded
            // dye is not a fallback in either case.
            chosen =
                ranked.find((c) => !excluded.has(c.dye.itemID) && !used.has(c.dye.itemID)) ??
                    ranked.find((c) => !excluded.has(c.dye.itemID)) ??
                    null;
        }
        else {
            chosen = ranked.find((c) => !excluded.has(c.dye.itemID)) ?? null;
        }
        if (chosen)
            used.add(chosen.dye.itemID);
        const companions = [];
        if (companionCount > 0) {
            for (const candidate of ranked) {
                if (companions.length >= companionCount)
                    break;
                if (excluded.has(candidate.dye.itemID))
                    continue;
                if (chosen && candidate.dye.itemID === chosen.dye.itemID)
                    continue;
                if (preventDuplicates && used.has(candidate.dye.itemID))
                    continue;
                companions.push(candidate.dye);
                if (preventDuplicates)
                    used.add(candidate.dye.itemID);
            }
        }
        slots.push({
            index,
            offset: normalisedOffset,
            targetHue,
            wheelHue,
            targetHex,
            dye: chosen?.dye ?? null,
            deviance: chosen?.deviance ?? 0,
            companions,
        });
    });
    return slots;
}
//# sourceMappingURL=HarmonySelector.js.map